import clsx from "clsx";
import React, { FunctionComponent, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useRoute } from "wouter";

import { productTypeWheel } from "../../../constants/productTypes";
import { useBreakpointMatch } from "../../../hooks/useBreakpointMatch";
import { DealerVehicleResultsRoute } from "../../../navigation/DealerRoutes";
import { useGetVehicleInfo } from "../../../queries/useGetVehicleInfo";
import { useSearchWheelsByVehicle } from "../../../queries/useSearchWheelsByVehicle";
import { LinearProgress } from "../../../ui/LinearProgress";
import { FiltersGroup } from "../../FiltersSidebar/FiltersGroup";
import {
  FiltersSidebar,
  useFiltersState,
} from "../../FiltersSidebar/FiltersSidebar";
import { NoData } from "../../NoData/NoData";
import { TireFilters } from "../../Tires/Results/Filters/TireFilters";
import { TireQuickSeasonFilters } from "../../Tires/Results/Filters/TireSeasonFilters/TireQuickSeasonFilters";
import {
  getUnavailableSeasonMessage,
  useTireResults,
} from "../../Tires/Results/useTireResults";
import { VehicleHeader } from "../../VehicleHeader/VehicleHeader";
import { Visualizer } from "../../Visualizer/Visualizer";
import { shouldRenderVisualizer } from "../../Visualizer/shouldRenderVisualizer";
import { WheelBrand } from "../../Wheels/Filters/WheelBrands/WheelBrands";
import { WheelPrice } from "../../Wheels/Filters/WheelPrice/WheelPrice";
import { CombosStatusbar } from "./CombosStatusbar";
import { Showcase } from "./Showcase/Showcase";

export const Combos: FunctionComponent = () => {
  const [isVehicle] = useRoute(DealerVehicleResultsRoute);
  const { data: vehicleInfo, isLoading: vehicleInfoIsLoading } =
    useGetVehicleInfo();
  const [, { hide }] = useFiltersState();
  const { t } = useTranslation();

  const { data: tires, isLoading: tiresIsLoading } = useTireResults();
  const { data: wheels, isLoading: wheelsIsLoading } =
    useSearchWheelsByVehicle();

  const hasVisualizer = shouldRenderVisualizer(
    vehicleInfo,
    wheels?.allParts.length || 0
  );

  const isMd = useBreakpointMatch("md");
  const portalRef = useRef<HTMLDivElement>(null);

  if (vehicleInfoIsLoading || wheelsIsLoading) {
    return <LinearProgress className="my-8 h-0.5" />;
  }

  const unavailableSeasonMessage = getUnavailableSeasonMessage(tires);
  if (unavailableSeasonMessage) {
    return (
      <>
        <TireQuickSeasonFilters className="my-4" />
        <NoData key="n">{t(unavailableSeasonMessage)}</NoData>
      </>
    );
  }

  if ([tires?.allParts.length, wheels?.allParts.length].includes(0)) {
    return <NoData key="n">{t("searchResults.noData")}</NoData>;
  }

  return (
    <>
      {hasVisualizer ? (
        <Visualizer
          controls={
            <>
              <TireQuickSeasonFilters className="!hidden xl:!flex" />
              <FiltersSidebar>
                <TireFilters onClose={hide}>
                  <FiltersGroup label={t(`dealerProducts.${productTypeWheel}`)}>
                    <WheelBrand />
                    <WheelPrice />
                  </FiltersGroup>
                </TireFilters>
              </FiltersSidebar>
            </>
          }
          className="mt-4"
          compact
          parts={wheels?.filteredParts || null}
          portalRef={portalRef}
          vehicleInfo={vehicleInfo || null}
        />
      ) : (
        <>
          <VehicleHeader className="mt-10 mx-4 lg:mx-0" />
          <CombosStatusbar className={clsx(isVehicle ? "md:mt-0" : "mt-4")}>
            <FiltersSidebar>
              <TireFilters onClose={hide}>
                <FiltersGroup label={t(`dealerProducts.${productTypeWheel}`)}>
                  <WheelBrand />
                  <WheelPrice />
                </FiltersGroup>
              </TireFilters>
            </FiltersSidebar>
          </CombosStatusbar>
        </>
      )}
      {[
        (tiresIsLoading || wheelsIsLoading) && (
          <LinearProgress className="my-8 h-0.5" />
        ),
        tires?.filteredParts.length && wheels?.filteredParts.length && (
          <Showcase
            portalRef={isMd && hasVisualizer ? portalRef : null}
            tires={tires}
            vehicleId={Number(vehicleInfo?.vehicleId) || null}
            wheels={wheels}
          />
        ),
        <NoData key="f">{t("searchResults.noData")}</NoData>,
      ].find(Boolean)}
    </>
  );
};

/**
 * @see docs/worklog/TW-859-combos-wheel-filters/rfc.md
 * @see docs/worklog/TW-859-combos-wheel-filters/tfc.md
 */
